import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import "./TechnicalAreaManager.scss";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const TechnicalAreaManager = () => {
  const [technicalAreaAdmin, setTechnicalAreaAdmin] = useState([]);
  const [technicalArea, setTechnicalArea] = useState({
    name: { az: "", en: "", ru: "" },
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const authToken = localStorage.getItem("authToken");

  const fetchTechnicalAreas = useCallback(() => {
    if (!authToken) return;

    setLoading(true);
    const api = axiosWithAuth();
    api.get("/technical-areas/admin")
      .then((response) => {
        // Log the response structure
        console.log("Technical areas API response:", response.data);
        
        // Process the data - note we're now using 'name' instead of 'title'
        const areas = Array.isArray(response.data) 
          ? response.data.map(area => ({
              ...area,
              // Ensure name property exists
              name: area.name || { az: "", en: "", ru: "" }
            }))
          : [];
          
        console.log("Transformed technical areas:", areas);
        setTechnicalAreaAdmin(areas);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch technical areas. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      Swal.fire({
        title: "Error",
        text: "Please login to access this page",
        icon: "error",
        timer: 1500,
      });
      return;
    }

    // Kullanıcı durumunu kontrol et
    const api = axiosWithAuth();
    api.get("/users/profile")
      .then((response) => {
        if (response.data.status !== 'active') {
          Swal.fire({
            title: "Error",
            text: "Your account is inactive. Please contact support.",
            icon: "error",
            timer: 3000,
          });
          return;
        }
        fetchTechnicalAreas();
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Error",
          text: "Failed to verify user status. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken, fetchTechnicalAreas]);

  const handleInput = (event) => {
    const { name, value, dataset } = event.target;
    const field = dataset.field;

    if (["az", "en", "ru"].includes(name)) {
      setTechnicalArea((prevType) => ({
        ...prevType,
        [field]: { ...prevType[field], [name]: value },
      }));
    } else {
      setTechnicalArea((prevType) => ({ ...prevType, [name]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!authToken) return;

    setLoading(true);
    const api = axiosWithAuth();
    const method = isEditing ? 'put' : 'post';
    const url = isEditing
      ? `/technical-areas/${editId}`
      : "/technical-areas";

    // Ensure we're using the correct field name (name) for the API
    const technicalAreaData = {
      name: technicalArea.name,
      status: technicalArea.status
    };

    api[method](url, technicalAreaData)
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setTechnicalArea({
          name: { az: "", en: "", ru: "" },
          status: true,
        });
        fetchTechnicalAreas();
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: isEditing ? "Technical area has been updated" : "Technical area has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: isEditing ? "Failed to update technical area" : "Failed to add technical area",
          icon: "error",
          timer: 1500,
        });
      });
  };

  const handleEdit = (type) => {
    // Title özelliğini güvenli şekilde ayarla
    const safeType = {
      ...type,
      name: type.name || { az: "", en: "", ru: "" }
    };
    setTechnicalArea(safeType);
    setEditId(type.id);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!authToken) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#cda742",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const api = axiosWithAuth();
        api.delete(`/technical-areas/${id}`)
          .then(() => {
            // Silinen kaydı listeden kaldır
            setTechnicalAreaAdmin(prevAreas => prevAreas.filter(area => area.id !== id));
            setLoading(false);
            Swal.fire({
              title: "Deleted!",
              text: "Technical area has been deleted.",
              icon: "success",
              timer: 1500,
            });
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
            Swal.fire({
              title: "Error",
              text: "Failed to delete technical area",
              icon: "error",
              timer: 1500,
            });
          });
      }
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditId(null);
    setTechnicalArea({
      name: { az: "", en: "", ru: "" },
      status: true,
    });
  };

  return (
    <div className="technical-area-manager">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Technical Area Manager</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} className="mb-4">
                  <div className="technical-area-name">
                    <h5 className="section-title">Technical Area Name</h5>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="az">
                            Azerbaijani
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={technicalArea.name.az}
                            onChange={handleInput}
                            name="az"
                            data-field="name"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="en">
                            English
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={technicalArea.name.en}
                            onChange={handleInput}
                            name="en"
                            data-field="name"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="ru">
                            Russian
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={technicalArea.name.ru}
                            onChange={handleInput}
                            name="ru"
                            data-field="name"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button 
                      type="submit" 
                      className="btn btn-primary px-4 me-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className={isEditing ? "bi bi-save me-2" : "bi bi-plus-circle me-2"}></i>
                      )}
                      {isEditing ? "Save Changes" : "Add Technical Area"}
                    </button>
                    {isEditing && (
                      <button 
                        type="button" 
                        className="btn btn-secondary px-4"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Cancel
                      </button>
                    )}
                  </div>
                </form>

                <div className="table-section">
                  <h5 className="section-title">Technical Areas List</h5>
                  {loading && !technicalAreaAdmin.length ? (
                    <div className="text-center my-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading technical areas...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th scope="col" className="text-center" style={{ width: "60px" }}>
                              №
                            </th>
                            <th scope="col">Name (az)</th>
                            <th scope="col">Name (en)</th>
                            <th scope="col">Name (ru)</th>
                            <th scope="col" className="text-center" style={{ width: "100px" }}>
                              Edit
                            </th>
                            <th scope="col" className="text-center" style={{ width: "100px" }}>
                              Delete
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {technicalAreaAdmin.length > 0 ? (
                            technicalAreaAdmin.map((type, index) => (
                              <tr key={type.id}>
                                <th scope="row" className="text-center">
                                  {index + 1}
                                </th>
                                <td>{type.name?.az || ""}</td>
                                <td>{type.name?.en || ""}</td>
                                <td>{type.name?.ru || ""}</td>
                                <td className="text-center">
                                  <button
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={() => handleEdit(type)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-pencil-square"></i>
                                    Edit
                                  </button>
                                </td>
                                <td className="text-center">
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDelete(type.id)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-trash3"></i>
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <i className="bi bi-exclamation-circle text-muted me-2"></i>
                                No technical areas found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalAreaManager;
